import * as React from 'react';
import { Capacitor } from '@capacitor/core';
import { useIdleReady } from '@/hooks/useIdleReady';
import {
  TRACKING_CONSENT_EVENT,
  hasTrackingConsent,
} from '@/utils/trackingManager';
import { shouldSuppressSensitiveAuthTelemetry } from '@/utils/urlPrivacy';

export function IdleAnalytics() {
  const isProd = import.meta.env.PROD;
  const ready = useIdleReady(1500);
  const isNativeRuntime = Capacitor.isNativePlatform();
  const suppressSensitiveAuthTelemetry =
    typeof window !== 'undefined' && shouldSuppressSensitiveAuthTelemetry(window.location.href);
  const [consentGranted, setConsentGranted] = React.useState<boolean>(() => hasTrackingConsent());
  const [AnalyticsComponent, setAnalyticsComponent] = React.useState<
    React.ComponentType | null
  >(null);
  const [SpeedInsightsComponent, setSpeedInsightsComponent] = React.useState<
    React.ComponentType | null
  >(null);

  React.useEffect(() => {
    if (isNativeRuntime) return;
    if (suppressSensitiveAuthTelemetry) return;
    if (typeof window === 'undefined') return;

    const syncConsent = () => {
      setConsentGranted(hasTrackingConsent());
    };

    syncConsent();
    window.addEventListener(TRACKING_CONSENT_EVENT, syncConsent);
    window.addEventListener('storage', syncConsent);

    return () => {
      window.removeEventListener(TRACKING_CONSENT_EVENT, syncConsent);
      window.removeEventListener('storage', syncConsent);
    };
  }, [isNativeRuntime, suppressSensitiveAuthTelemetry]);

  React.useEffect(() => {
    if (isNativeRuntime) return;
    if (suppressSensitiveAuthTelemetry) return;
    if (!isProd) return;
    if (!ready) return;
    if (!consentGranted) return;

    let active = true;

    import('@vercel/analytics/react')
      .then((mod) => {
        if (active) setAnalyticsComponent(() => mod.Analytics);
      })
      .catch(() => {});

    import('@vercel/speed-insights/react')
      .then((mod) => {
        if (active) setSpeedInsightsComponent(() => mod.SpeedInsights);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [ready, isProd, consentGranted, isNativeRuntime, suppressSensitiveAuthTelemetry]);

  if (isNativeRuntime || suppressSensitiveAuthTelemetry || !isProd || !ready || !consentGranted) {
    return null;
  }

  return (
    <>
      {AnalyticsComponent ? <AnalyticsComponent /> : null}
      {SpeedInsightsComponent ? <SpeedInsightsComponent /> : null}
    </>
  );
}

export default IdleAnalytics;
